/**
 * FiscalPrinterContext
 * Provides access to fiscal printer API endpoints
 */

import {
  CreateUpdateExamDtoPaymentTypeEnum,
  DefaultApi,
  GenerateReceiptDto,
  GenerateReceiptDtoPaymentMethodEnum,
  ReceiptItemDto,
  XmlProtocolResponseDto,
} from '../autogenerated/spdApiClient';
import { printerService } from '../../printer/printerService';
import { PaymentFormEnum } from '../validation/enums';

export const RECEIPT_DEFAULT_ITEM: ReceiptItemDto = {
  name: '',
  quantity: 1,
  price: '',
  vat: 'zw.',
};

export const mapPaymentMethodToType = (
  method: GenerateReceiptDtoPaymentMethodEnum,
): CreateUpdateExamDtoPaymentTypeEnum => {
  const map: Record<string, CreateUpdateExamDtoPaymentTypeEnum> = {
    [PaymentFormEnum.Cash]: CreateUpdateExamDtoPaymentTypeEnum.Gotwka,
    [PaymentFormEnum.Card]: CreateUpdateExamDtoPaymentTypeEnum.Karta,
    [PaymentFormEnum.BankTransfer]: CreateUpdateExamDtoPaymentTypeEnum.Przelew,
  };
  return map[method as string] ?? CreateUpdateExamDtoPaymentTypeEnum.Gotwka;
};

export interface FiscalPrinterContextInterface {
  generateAndPrintReceipt: (
    generateReceiptDto: Omit<GenerateReceiptDto, 'printerUrl'>,
  ) => Promise<{
    jpkid: number;
    receiptId: string;
    printerUid: string | null;
    info?: string;
  }>;
  generateEReceiptConfig: () => Promise<XmlProtocolResponseDto>;
}

export const FiscalPrinterContext = (
  api: DefaultApi,
): FiscalPrinterContextInterface => ({
  generateAndPrintReceipt: async (
    generateReceiptDto: Omit<GenerateReceiptDto, 'printerUrl'>,
  ): Promise<{
    jpkid: number;
    receiptId: string;
    printerUid: string | null;
    info?: string;
  }> => {
    const response = await api.fiscalPrinterControllerGenerateAndPrintReceipt({
      ...generateReceiptDto,
      printerUrl: printerService.getConfig()?.apiUrl ?? '',
    });
    if (response.token && response.tokenExpiresAt) {
      printerService.seedToken(response.token, response.tokenExpiresAt);
    }
    const status = await printerService.printReceipt(response.payload);
    return {
      jpkid: status.jpkid,
      receiptId: response.receiptId,
      printerUid: printerService.getStoredUniqueId(),
      info: status.info ?? undefined,
    };
  },
  generateEReceiptConfig: async (): Promise<XmlProtocolResponseDto> => {
    return await api.fiscalPrinterControllerGenerateEReceiptConfig();
  },
});
